import { useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRight, Check, ChevronRight, Sparkles, UserRoundPen } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { Card, Emblema, EstadoVacio, Pastilla, Seccion } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { NumberField, SelectField } from '@/components/Field';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { leerMisAlimentos } from '@/db/alimentos';
import { leerDiaDeComida } from '@/db/comidas';
import {
  comidasConAlimentosDe,
  comidasQueFaltan,
  crearComidasHabituales,
  habitualesDe,
  plantillaHabitualDe,
  registrarHabitual,
  revisarAlimentos,
} from '@/db/habituales';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { ComidaHabitual, Food, IngredientePlatillo, MealEntry, Nutrientes, PersonaRutinas } from '@/db/types';
import type { AlimentoDePlantilla, ComidaDePlantilla } from '@/datos/comidas-janeth';
import { PLANTILLAS_DE_RUTINAS } from '@/datos/rutinas-precargadas';
import { ahoraIso, diaSemana, nombreDia, type FechaLocal } from '@/domain/dates';
import { fechaDelDia } from '@/domain/hoy';
import { horaActual, esHoraLocal } from '@/domain/time';
import { leerDecimal } from '@/domain/numbers';
import {
  NOMBRE_TIEMPO,
  NOTAS_CALIDAD,
  formatearGramos,
  formatearKcal,
  opcionesDePorcion,
  textoCantidad,
  textoMacros,
  textoValores,
} from '@/domain/nutricion';
import { sumarNutrientes } from '@/domain/platillos';
import {
  calcularItem,
  calidadMostrada,
  crearResolverHabitual,
  entradasDeHabitual,
  registradaEn,
  subtotal,
  textoDiasHabitual,
  type AlimentoDeHabitual,
  type ItemCalculado,
  type ResolverHabitual,
} from '@/domain/habituales';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { useAlimentosBase, type EstadoBase } from '@/lib/useAlimentosBase';
import { ElegirPersona } from '@/features/gym/Precargadas';
import { CampoHora, Etiqueta, EtiquetaCalidad, VistaValores, fechaDeParametro, rutaDelDia } from './comunes';

/**
 * Comidas habituales (0.3.5): el desayuno y la comida que se repiten de lunes
 * a viernes, como plantillas.
 *
 * - Se revisan antes de crearlas, con la fuente y la calidad de cada dato.
 * - En Comida se proponen en su día; cuentan solo al confirmar lo que se
 *   comió, con las cantidades de ese día. Nada se registra solo ni hacia
 *   atrás, no hay cena fija y no cambian metas ni el plan.
 */

const VOLVER = { a: '/comida', texto: 'Comida', etiqueta: 'Volver a Comida' };

const AVISO_SUBTOTAL =
  'Es el subtotal de esta comida, no lo que comes en todo el día ni una meta. La cena y lo demás se suman cuando los registras.';

const SIN_PLANTILLA: readonly AlimentoDePlantilla[] = [];

/** Los alimentos de la base y de Mis alimentos como están hoy; con la plantilla, también los que se van a crear. */
function useResolverHabitual(plantilla: readonly AlimentoDePlantilla[] = SIN_PLANTILLA): {
  base: EstadoBase;
  propios: Food[] | undefined;
  resolver: ResolverHabitual | null;
} {
  const base = useAlimentosBase();
  const propios = useLiveQuery(() => leerMisAlimentos(), []);
  const resolver = useMemo(() => {
    if (base.estado !== 'lista' || propios === undefined) return null;
    const mapa = new Map<string, Food | AlimentoDePlantilla>(propios.map((f) => [f.id, f]));
    for (const a of plantilla) if (!mapa.has(a.id)) mapa.set(a.id, a);
    return crearResolverHabitual(base.modulo.alimentoBase, mapa);
  }, [base, propios, plantilla]);
  return { base, propios, resolver };
}

/** "Referencia genérica, no la etiqueta de tu producto. USDA FDC 170554." */
function textoFuente(a: AlimentoDeHabitual): string | null {
  const calidad = calidadMostrada(a);
  if (calidad === null) return null;
  const usda = a.fuenteBase === null ? '' : ` USDA FDC ${a.fuenteBase.fdcId}.`;
  return `${NOTAS_CALIDAD[calidad]}${usda}`;
}

const cantidadDe = (item: Pick<IngredientePlatillo, 'cantidad' | 'porcion'>, gramos: number | null) =>
  textoCantidad({ cantidad: item.cantidad, porcion: item.porcion, gramos }) ?? '';

/** "P 25 g · C 0.5 g · G 2.4 g · fibra 0.4 g" */
const macrosYFibra = (v: Nutrientes) =>
  [textoMacros(v), v.fibraG === null ? null : `fibra ${formatearGramos(v.fibraG)}`].filter(Boolean).join(' · ');

// ---------------------------------------------------------------------------
// Lista y revisión: /comida/habituales
// ---------------------------------------------------------------------------

export function HabitualesPage() {
  const hoy = useFechaHoy();
  const navegar = useNavigate();
  const idTitulo = useId();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const deHoy = useLiveQuery(() => leerDiaDeComida(hoy), [hoy]);
  const persona = ajustes?.rutinasDe ?? null;
  const plantilla = plantillaHabitualDe(persona);
  const { base, propios, resolver } = useResolverHabitual(plantilla?.alimentos);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (ajustes === undefined || deHoy === undefined || propios === undefined || base.estado === 'cargando') return null;

  async function cambiarPersona() {
    setError(null);
    try {
      await actualizarSettings({ rutinasDe: null });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  if (persona === null) {
    return (
      <div className="space-y-6">
        <PageHeader
          antetitulo="Comidas habituales"
          titulo="¿De quién es este teléfono?"
          descripcion="Elige una vez y te mostramos sus comidas habituales para revisarlas y crearlas. Elegir no cambia ni registra nada."
          volver={VOLVER}
        />
        <p id={idTitulo} className="sr-only">
          ¿De quién es este teléfono?
        </p>
        <ElegirPersona etiquetadoPor={idTitulo} />
      </div>
    );
  }

  const existentes = habitualesDe(ajustes);
  const faltan = plantilla === null ? [] : comidasQueFaltan(existentes, plantilla.comidas);

  if (existentes.length === 0 && faltan.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Comidas habituales." volver={VOLVER} />
        <EstadoVacio
          titulo="Todavía no hay comidas habituales para este teléfono."
          descripcion={`Cuando ${PLANTILLAS_DE_RUTINAS[persona].nombrePersona} mande las suyas, aparecen aquí para revisarlas. Lo que registras en Comida no cambia.`}
        />
        <Button variante="fantasma" icono={UserRoundPen} onClick={() => void cambiarPersona()}>
          No es mi teléfono: elegir otra persona
        </Button>
      </div>
    );
  }

  if (base.estado === 'error' || resolver === null) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Comidas habituales." volver={VOLVER} />
        <Banner tono="alerta" titulo="No se pudo abrir la base de alimentos">
          Cierra y vuelve a abrir la app.
        </Banner>
      </div>
    );
  }

  if (faltan.length > 0 && plantilla !== null) {
    const revision = revisarAlimentos(plantilla.alimentos, propios);
    const elegida: PersonaRutinas = persona;
    async function crear() {
      if (guardando) return;
      setGuardando(true);
      setError(null);
      try {
        await crearComidasHabituales(elegida, hoy);
        void navegar('/comida', { replace: true });
      } catch (e) {
        setError(mensajeDeError(e));
        setGuardando(false);
      }
    }
    return (
      <div className="space-y-6">
        <PageHeader
          antetitulo="Tus comidas habituales"
          titulo="Desayuno y comida."
          descripcion="De lunes a viernes, como plantillas: se proponen en su día y cuentan solo cuando confirmas lo que comiste. Cada día puedes cambiar las cantidades."
          volver={VOLVER}
        />
        <Banner tono="info" titulo="Nada se registra solo">
          No se llenan días hacia atrás ni el sábado o el domingo, y la cena la capturas cada día. Tus metas y tu plan 6 + 2 no
          cambian.
        </Banner>
        {revision.conOtrosValores.length > 0 && (
          <Banner tono="aviso" titulo="Ya tienes alimentos con el mismo nombre">
            <ul className="grid gap-1">
              {revision.conOtrosValores.map(({ plantilla: p }) => (
                <li key={p.id}>«{p.nombre}»: no es igual al de tu archivo, así que se agrega el del archivo; el tuyo no cambia.</li>
              ))}
            </ul>
          </Banner>
        )}
        {/* Con los alimentos que se van a usar: si ya tenías uno igual, el tuyo */}
        <ListaDeComidas comidas={comidasConAlimentosDe(plantilla.comidas, revision)} existentes={existentes} resolver={resolver} />
        {error !== null && (
          <Banner tono="alerta" titulo="No se crearon">
            {error}
          </Banner>
        )}
        <div className="grid gap-2">
          <Button icono={Check} tamano="grande" disabled={guardando} onClick={() => void crear()}>
            {guardando ? 'Creando…' : 'Crear mis comidas habituales'}
          </Button>
          <Button variante="fantasma" icono={UserRoundPen} onClick={() => void cambiarPersona()}>
            No es mi teléfono: elegir otra persona
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo="Comidas habituales"
        titulo="Tus comidas habituales."
        descripcion="Se proponen en Comida en sus días y cuentan cuando las confirmas, con las cantidades de ese día."
        volver={VOLVER}
      />
      {existentes.map((h) => (
        <ComidaDeLaLista
          key={h.id}
          comida={h}
          resolver={resolver}
          pie={
            registradaEn(h, deHoy.entradas) ? (
              <p className="text-[13px] text-texto-suave">Hoy ya la registraste.</p>
            ) : (
              <BotonEnlace to={`/comida/habitual/${h.id}?fecha=${hoy}`} variante="secundario" icono={ArrowRight}>
                Revisar y registrar hoy
              </BotonEnlace>
            )
          }
        />
      ))}
      <p className="text-[12px] leading-relaxed text-texto-suave">
        Sus valores salen de tus alimentos como están hoy: si corriges uno en Mis alimentos, cambia lo que registres después;
        lo ya registrado no cambia. Tus metas y tu plan 6 + 2 no cambian.
      </p>
    </div>
  );
}

/** Las comidas de la plantilla con sus valores y el subtotal de las dos, antes de crearlas. */
function ListaDeComidas({
  comidas,
  existentes,
  resolver,
}: {
  comidas: readonly ComidaDePlantilla[];
  existentes: readonly ComidaHabitual[];
  resolver: ResolverHabitual;
}) {
  const subtotales = comidas.map((c) => subtotal(c.items.map((i) => calcularItem(i, resolver))));
  const todos = subtotales.every((s) => s !== null) ? sumarNutrientes(subtotales as Nutrientes[]) : null;
  return (
    <>
      {comidas.map((c) => (
        <ComidaDeLaLista
          key={c.id}
          comida={c}
          resolver={resolver}
          pie={existentes.some((h) => h.id === c.id) ? <p className="text-[13px] text-texto-suave">Ya está en tus comidas habituales.</p> : null}
        />
      ))}
      {todos !== null && comidas.length > 1 && (
        <div className="rounded-panel bg-superficie p-5">
          <span className="antetitulo">{comidas.map((c) => NOMBRE_TIEMPO[c.tiempo]).join(' y ')}, sin cena</span>
          <p className="mt-2 text-[30px] leading-none tracking-[-1px] tabular-nums text-texto">{formatearKcal(todos.kcal)}</p>
          <p className="mt-1.5 text-[12px] tabular-nums text-texto-suave">{macrosYFibra(todos)}</p>
          <p className="mt-3 text-[12px] leading-relaxed text-texto-suave">
            Es el subtotal de esas comidas, no lo que comes en todo el día ni una meta diaria.
          </p>
        </div>
      )}
    </>
  );
}

function ComidaDeLaLista({
  comida,
  resolver,
  pie,
}: {
  comida: Pick<ComidaHabitual, 'nombre' | 'tiempo' | 'dias' | 'items'>;
  resolver: ResolverHabitual;
  pie: ReactNode;
}) {
  const calculados = comida.items.map((i) => calcularItem(i, resolver));
  const suma = subtotal(calculados);
  return (
    <Seccion titulo={comida.nombre} extra={textoDiasHabitual(comida.dias)}>
      <ul className="divide-y divide-borde">
        {calculados.map((c, i) => (
          <AlimentoDeLaLista key={`${c.item.alimentoId}-${i}`} calculado={c} />
        ))}
      </ul>
      <p className="text-[13px] tabular-nums text-texto">
        {suma === null ? 'Revisa sus alimentos: alguno no se puede calcular.' : `Subtotal: ${textoValores(suma)}`}
      </p>
      {pie}
    </Seccion>
  );
}

function AlimentoDeLaLista({ calculado }: { calculado: ItemCalculado }) {
  const { item, alimento, valores, gramos, error } = calculado;
  const fuente = alimento === null ? null : textoFuente(alimento);
  return (
    <li className="py-3">
      <p className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-medium text-texto">{alimento?.nombre ?? 'Alimento que ya no existe'}</span>
          <EtiquetaCalidad calidad={alimento === null ? null : calidadMostrada(alimento)} />
        </span>
        <span className="whitespace-nowrap text-[13px] tabular-nums text-texto">
          {valores === null ? '—' : formatearKcal(valores.kcal)}
        </span>
      </p>
      <p className="text-[12px] tabular-nums text-texto-suave">
        {[cantidadDe(item, gramos), valores === null ? error : macrosYFibra(valores)].filter(Boolean).join(' · ')}
      </p>
      {fuente !== null && <p className="text-[12px] text-texto-suave">{fuente}</p>}
      {alimento?.nota !== null && alimento?.nota !== undefined && (
        <details className="mt-1">
          <summary className="inline-flex min-h-11 cursor-pointer items-center text-[12px] font-medium text-marca">
            Fuente y detalles
          </summary>
          <p className="text-[12px] leading-relaxed text-texto-suave">{alimento.nota}</p>
        </details>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Confirmar lo que se comió: /comida/habitual/:id?fecha=
// ---------------------------------------------------------------------------

export function ConfirmarHabitualPage() {
  const { id = '' } = useParams();
  const [parametros] = useSearchParams();
  const hoy = useFechaHoy();
  const fecha = fechaDeParametro(parametros.get('fecha'), hoy);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const dia = useLiveQuery(() => leerDiaDeComida(fecha), [fecha]);
  const { base, propios, resolver } = useResolverHabitual();

  if (ajustes === undefined || dia === undefined || propios === undefined || base.estado === 'cargando') return null;
  const habitual = habitualesDe(ajustes).find((h) => h.id === id);
  if (habitual === undefined) {
    return (
      <div>
        <PageHeader titulo="No está." volver={VOLVER} />
        <EstadoVacio titulo="Esa comida habitual no está en este teléfono." descripcion="Lo que ya registraste no cambia." />
      </div>
    );
  }
  if (base.estado === 'error' || resolver === null) {
    return (
      <div className="space-y-6">
        <PageHeader titulo={habitual.nombre} volver={VOLVER} />
        <Banner tono="alerta" titulo="No se pudo abrir la base de alimentos">
          Cierra y vuelve a abrir la app.
        </Banner>
      </div>
    );
  }
  return (
    <FormularioHabitual
      key={`${habitual.id}|${fecha}`}
      habitual={habitual}
      fecha={fecha}
      hoy={hoy}
      resolver={resolver}
      entradasDelDia={dia.entradas}
    />
  );
}

interface Fila {
  incluido: boolean;
  cantidad: string;
  porcion: string;
}

function FormularioHabitual({
  habitual,
  fecha,
  hoy,
  resolver,
  entradasDelDia,
}: {
  habitual: ComidaHabitual;
  fecha: FechaLocal;
  hoy: FechaLocal;
  resolver: ResolverHabitual;
  entradasDelDia: readonly MealEntry[];
}) {
  const navegar = useNavigate();
  // Los ids se crean al abrir: tocar dos veces "Registrar" no duplica (Req. 1.9)
  const [abierto] = useState(() => ({
    ids: habitual.items.map(() => nuevoId('com')),
    hora: horaActual(),
    creadoEn: ahoraIso(),
  }));
  // Una porción que ya no tiene el alimento (por ejemplo, si se le cambió el nombre en Mis
  // alimentos) toma la primera que tenga, como al registrar: lo que se ve es lo que se calcula
  const [filas, setFilas] = useState<Fila[]>(() =>
    habitual.items.map((i) => {
      const alimento = resolver(i);
      const opciones = alimento === null ? [] : opcionesDePorcion(alimento.calculable);
      const porcion = alimento === null || opciones.some((o) => o.clave === i.porcion) ? i.porcion : (opciones[0]?.clave ?? i.porcion);
      return { incluido: true, cantidad: String(i.cantidad), porcion };
    }),
  );
  const [hora, setHora] = useState(abierto.hora);
  const [comoHabituales, setComoHabituales] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tiempo = NOMBRE_TIEMPO[habitual.tiempo].toLocaleLowerCase('es-MX');
  const encabezado = (
    <PageHeader
      antetitulo={`${NOMBRE_TIEMPO[habitual.tiempo]} · ${fechaDelDia(fecha)}`}
      titulo={habitual.nombre}
      descripcion="Marca lo que comiste y ajusta las cantidades. No cuenta hasta que lo registres."
      volver={{ a: rutaDelDia(fecha, hoy, habitual.tiempo), texto: 'Comida', etiqueta: 'Volver a Comida' }}
    />
  );

  // Ya registrada ese día con otros registros (los de esta pantalla son los mismos ids)
  const otra = entradasDelDia.some((e) => e.habitual === habitual.id && !abierto.ids.includes(e.id));
  if (otra) {
    return (
      <div className="space-y-6">
        {encabezado}
        <Banner tono="exito" titulo={`Ya registraste tu ${habitual.nombre.toLocaleLowerCase('es-MX')} ese día`}>
          Para cambiar algo, ábrelo en el día. Así no se cuenta dos veces.
        </Banner>
        <BotonEnlace to={rutaDelDia(fecha, hoy, habitual.tiempo)} variante="secundario" icono={ArrowRight}>
          Ver ese día
        </BotonEnlace>
      </div>
    );
  }
  if (fecha > hoy) {
    return (
      <div className="space-y-6">
        {encabezado}
        <Banner tono="info" titulo="Todavía no llega ese día">
          Se registra cuando lo comas, nunca por adelantado.
        </Banner>
      </div>
    );
  }

  const elecciones = filas.map((f) => ({ incluido: f.incluido, cantidad: leerDecimal(f.cantidad), porcion: f.porcion }));
  const calculados = habitual.items.map((item, i) => {
    const e = elecciones[i];
    return calcularItem(
      { ...item, cantidad: e?.cantidad ?? Number.NaN, porcion: e?.porcion ?? item.porcion },
      resolver,
    );
  });
  const incluidos = calculados.filter((_, i) => filas[i]?.incluido === true);
  const suma = incluidos.length === 0 ? null : subtotal(incluidos);
  const resultado = entradasDeHabitual(habitual, elecciones, resolver, {
    ids: abierto.ids,
    fecha,
    hora,
    creadoEn: abierto.creadoEn,
  });
  const motivo = resultado.ok ? null : resultado.error;

  function cambiar(i: number, cambios: Partial<Fila>) {
    setFilas((fs) => fs.map((f, n) => (n === i ? { ...f, ...cambios } : f)));
  }

  async function registrar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando || !resultado.ok) return;
    if (!esHoraLocal(hora)) return setError('Elige la hora.');
    setError(null);
    setGuardando(true);
    try {
      // Lo que no se marcó conserva su cantidad habitual
      const cantidades = comoHabituales
        ? habitual.items.map((item, i) => {
            const e = elecciones[i];
            return e !== undefined && e.incluido && e.cantidad !== null
              ? { cantidad: e.cantidad, porcion: e.porcion }
              : { cantidad: item.cantidad, porcion: item.porcion };
          })
        : null;
      await registrarHabitual(habitual.id, resultado.entradas, { cantidades, hoy });
      void navegar(rutaDelDia(fecha, hoy, habitual.tiempo), { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  // Lo que ya se registró a mano en ese tiempo, para no contarlo dos veces
  const yaHay = entradasDelDia.filter((e) => e.tiempo === habitual.tiempo && !abierto.ids.includes(e.id)).length;

  return (
    <div>
      {encabezado}
      {!habitual.dias.includes(diaSemana(fecha)) && (
        <div className="mb-5">
          <Banner tono="info" titulo={`El ${nombreDia(fecha)} no es de tus días habituales`}>
            Puedes registrarla igual si la comiste.
          </Banner>
        </div>
      )}
      {yaHay > 0 && (
        <div className="mb-5">
          <Banner
            tono="aviso"
            titulo={`Ya ${yaHay === 1 ? 'hay 1 registro' : `hay ${yaHay} registros`} en tu ${tiempo} de ese día`}
          >
            Desmarca lo que ya registraste: así no se cuenta dos veces.
          </Banner>
        </div>
      )}
      <form onSubmit={(e) => void registrar(e)} className="grid gap-[17px]" noValidate>
        <ul className="grid gap-3">
          {habitual.items.map((item, i) => (
            <FilaDeHabitual
              key={`${item.alimentoId}-${i}`}
              calculado={calculados[i] as ItemCalculado}
              fila={filas[i] as Fila}
              onCambio={(c) => cambiar(i, c)}
            />
          ))}
        </ul>

        {suma !== null ? (
          <VistaValores valores={suma} gramos={null} nota={AVISO_SUBTOTAL} />
        ) : (
          <p className="rounded-panel bg-superficie p-5 text-[13px] text-texto-suave">
            {motivo ?? 'Marca lo que comiste para ver sus valores.'}
          </p>
        )}

        <CampoHora etiqueta="Hora" valor={hora} onCambio={setHora} />

        <Casilla marcada={comoHabituales} onCambio={setComoHabituales}>
          Guardar estas cantidades como las habituales
        </Casilla>

        {suma !== null && motivo !== null && (
          <Banner tono="alerta" titulo="No se puede registrar">
            {motivo}
          </Banner>
        )}
        {error !== null && (
          <Banner tono="alerta" titulo="No se registró">
            {error}
          </Banner>
        )}
        <Button type="submit" icono={Check} disabled={guardando || motivo !== null}>
          {guardando ? 'Registrando…' : `Registrar ${tiempo}`}
        </Button>
      </form>
    </div>
  );
}

function FilaDeHabitual({
  calculado,
  fila,
  onCambio,
}: {
  calculado: ItemCalculado;
  fila: Fila;
  onCambio: (cambios: Partial<Fila>) => void;
}) {
  const { alimento, valores, error } = calculado;
  if (alimento === null) {
    return (
      <li className="rounded-panel bg-superficie p-4">
        <Casilla marcada={fila.incluido} onCambio={(m) => onCambio({ incluido: m })}>
          <span className="sr-only">Lo comí: </span>
          <span className="font-medium">Alimento que ya no existe</span>
        </Casilla>
        <p className="mt-2 text-[12px] text-texto-suave">Desmárcalo para registrar los demás.</p>
      </li>
    );
  }
  const opciones = opcionesDePorcion(alimento.calculable);
  const fuente = textoFuente(alimento);
  return (
    <li className="space-y-3 rounded-panel bg-superficie p-4">
      <Casilla marcada={fila.incluido} onCambio={(m) => onCambio({ incluido: m })}>
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="sr-only">Lo comí: </span>
          <span className="font-medium">{alimento.nombre}</span>{' '}
          <EtiquetaCalidad calidad={calidadMostrada(alimento)} />
        </span>
      </Casilla>
      {fila.incluido ? (
        <>
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
            <NumberField
              etiqueta="Cantidad"
              complemento={`de ${alimento.nombre}`}
              valor={fila.cantidad}
              onCambio={(v) => onCambio({ cantidad: v })}
            />
            <SelectField
              etiqueta="Porción"
              complemento={`de ${alimento.nombre}`}
              valor={fila.porcion}
              onCambio={(v) => onCambio({ porcion: v })}
              opciones={opciones.map((o) => ({ valor: o.clave, etiqueta: o.etiqueta }))}
            />
          </div>
          <p className="text-[12px] tabular-nums text-texto" aria-live="polite">
            {valores !== null
              ? textoValores(valores)
              : fila.cantidad.trim() === ''
                ? 'Escribe la cantidad.'
                : (error ?? 'Revisa la cantidad.')}
          </p>
        </>
      ) : (
        <p className="text-[12px] text-texto-suave">No se registra.</p>
      )}
      {fuente !== null && <p className="text-[12px] leading-relaxed text-texto-suave">{fuente}</p>}
    </li>
  );
}

// ---------------------------------------------------------------------------
// En Comida
// ---------------------------------------------------------------------------

/**
 * Arriba del día, una sola vez: sin elegir de quién es el teléfono, lo
 * pregunta; elegido, ofrece revisar y crear sus comidas habituales. Si ya
 * están, o si esa persona no tiene, no muestra nada.
 */
export function TarjetaHabituales({
  persona,
  existentes,
}: {
  persona: PersonaRutinas | null;
  existentes: readonly ComidaHabitual[];
}) {
  const idTitulo = useId();
  if (persona === null) {
    return (
      <Card>
        <div className="flex items-start justify-between gap-3">
          <Emblema icono={Sparkles} />
          <Pastilla>Una sola vez</Pastilla>
        </div>
        <span className="antetitulo mt-3 block">Comidas habituales</span>
        <h2 id={idTitulo} className="mb-2 mt-2 text-[22px] leading-tight tracking-[-0.6px] text-texto">
          ¿De quién es este teléfono?
        </h2>
        <p className="mb-4 text-[13px] leading-relaxed text-texto-suave">
          Janeth tiene su desayuno y su comida habituales listos para revisar. Elegir no cambia ni registra nada.
        </p>
        <ElegirPersona etiquetadoPor={idTitulo} />
      </Card>
    );
  }
  const plantilla = plantillaHabitualDe(persona);
  if (plantilla === null) return null;
  const faltan = comidasQueFaltan(existentes, plantilla.comidas);
  if (faltan.length === 0) return null;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <Emblema icono={Sparkles} />
        <Pastilla>Lun a Vie</Pastilla>
      </div>
      <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">Tu desayuno y tu comida.</h2>
      <p className="mb-4 text-[13px] leading-relaxed text-texto-suave">
        Como plantillas de lunes a viernes, con las cantidades de tu archivo: cuentan cuando confirmas lo que comiste. Revísalas
        antes de crearlas.
      </p>
      <BotonEnlace to="/comida/habituales" icono={ArrowRight} variante="secundario">
        Revisar y crear
      </BotonEnlace>
    </Card>
  );
}

/** En su tiempo de comida, el día que toca y mientras no se registre: propone confirmarla. */
export function AvisoHabitual({ habitual, fecha }: { habitual: ComidaHabitual; fecha: FechaLocal }) {
  const n = habitual.items.length;
  return (
    <Link
      to={`/comida/habitual/${habitual.id}?fecha=${fecha}`}
      className="mt-2 flex min-h-14 items-center gap-3 rounded-[17px] border border-dashed border-borde-fuerte px-3 py-3 active:opacity-60"
    >
      <span className="grid min-w-0 flex-1 gap-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-medium text-texto">{habitual.nombre}</span>
          <Etiqueta tono="aviso">Sin registrar</Etiqueta>
        </span>
        <span className="text-[12px] text-texto-suave">
          No cuenta hasta que lo confirmes · {n === 1 ? '1 alimento' : `${n} alimentos`}
        </span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-texto-suave" />
    </Link>
  );
}
